import React, { useEffect, useState } from "react"
import useToggle from "hooks/use-toggle"
import Portal from "@kit/design/portal"
import Icon from "@kit/design/icon"
import Stack from "@kit/design/stack"
import VisuallyHidden from "@kit/design/visually-hidden"
import Avatar from "core/avatar"
import useFocusTrap from "./utils/use-focus-trap"
import { menu as menudefinitions, settings } from "./utils/menu-definitions"
import useAccountOptions from "./utils/use-account-options"
import { showLinksByFeature } from "./primary-nav"
import MobileCtaNav from "./mobile-cta-nav"
import useCurrentUser from "hooks/use-current-user"
import analytics from "utils/analytics"
import { slugify } from "core/utils/slugify"

const MenuButton = ({ name, children, active, updateActive }) => {
  const [expanded, setExpanded] = useState(false)
  const iconTransform =
    expanded && name === active ? "transform -rotate-180" : ""

  const toggle = () => {
    const willExpand = name === active ? !expanded : true
    if (willExpand) {
      analytics.track(
        "Element",
        "Clicked",
        {
          element_type: "button",
          name: `nav_section_${slugify(name)}`,
          text: name,
          location: "mobile_nav"
        },
        { transport: "beacon" }
      )
    }
    updateActive(name)
    setExpanded(currentState => (name === active ? !currentState : true))
  }

  return (
    <div className="bg-white">
      <button
        role="menuitem"
        aria-haspopup="true"
        aria-expanded={expanded}
        onClick={toggle}
        className="flex w-full py-4 px-8 justify-between items-center border-b border-gray-100 focus:outline-none focus-visible:bg-gray-50"
      >
        <div className="font-semibold text-gray-700 capitalize">{name}</div>
        <Icon
          name="down"
          className={`inline-block text-xl transition ease-in-out duration-200 ${iconTransform}`}
        />
      </button>
      {expanded && name === active ? (
        <ul
          role="menu"
          aria-label={name}
          className="list-none py-2 px-0 m-0 border-b border-gray-100"
        >
          {children}
        </ul>
      ) : null}
    </div>
  )
}

const Submenu = ({
  label,
  icon,
  data,
  section,
  color = "",
  href = "/",
  method = "get",
  onClick
}) => {
  const menuLabel = label.toLowerCase()

  function trackClick() {
    analytics.track(
      "Element",
      "Clicked",
      {
        element_type: "link",
        name: `nav_${slugify(label)}`,
        text: label,
        location: "mobile_nav",
        section
      },
      { transport: "beacon" }
    )
  }

  if (onClick) {
    return (
      <li role="none">
        <button
          role="menuitem"
          onClick={() => {
            trackClick()
            onClick()
          }}
          className="block w-full text-left py-4 px-8 hover:no-underline focus-visible:bg-gray-50"
        >
          <Icon
            name={icon}
            className={`inline-block mr-4 ${color} text-2xl align-bottom`}
          />
          <span className="capitalize font-semibold text-gray-800">
            {label}
          </span>
        </button>
      </li>
    )
  }

  if (data) {
    return (
      <li role="none" className="py-4 px-8">
        <hr className="m-0 mb-8" />
        <div className="mb-4 font-semibold text-gray-900">{label}</div>
        <Stack spacing={2}>
          {data.map(link => (
            <a
              href={link.href}
              key={link.id}
              data-method={method}
              className="flex items-center gap-2 w-full rounded text-left py-2 px-1 hover:no-underline focus-visible:bg-gray-50"
            >
              <Avatar email={link.email} className="inline-block" />
              <VisuallyHidden>{link.label}</VisuallyHidden>
              <span
                className={`align-middle ${color} w-full truncate whitespace-nowrap`}
                title={link.label}
                aria-hidden="true"
              >
                {link.label}
              </span>
            </a>
          ))}
        </Stack>
      </li>
    )
  }

  return (
    <li role="none">
      <a
        href={href}
        data-method={method}
        onClick={trackClick}
        className="block w-full text-left py-4 px-8 hover:no-underline focus-visible:bg-gray-50"
      >
        <Icon
          name={icon}
          className={`inline-block mr-4 ${color} text-2xl align-bottom`}
        />
        <span className="capitalize font-semibold text-gray-800">{label}</span>
      </a>
      {menuLabel === "view account in admin" && <hr className="mx-8 my-4" />}
    </li>
  )
}

// eslint-disable-next-line import/no-default-export
export default function MobileNav({ userInfo }) {
  const [expanded, toggle] = useToggle(false)
  const [activeMenu, setActive] = useState()
  const settingOptions = useAccountOptions(userInfo)

  const user = useCurrentUser()
  const cksnProgrammaticAdsEnabled = user?.canViewAds
  const canViewPaidRecommendations = user?.canUsePaidRecommendations

  // Traps focus within this nav and its toggle button outside it
  useFocusTrap("#mobileMenu", "#mobileNavToggle")

  // Toggles tailwind fixed class on body when mobile menu is open
  useEffect(() => {
    document.body.classList.toggle("fixed--full", expanded)
    if (!expanded) {
      setActive(undefined)
    }
  }, [expanded])

  return (
    <div className="lg:hidden">
      <button
        aria-haspopup="true"
        aria-expanded={expanded}
        aria-controls="mobileMenu"
        onClick={toggle}
        id="mobileNavToggle"
        className="relative overflow-hidden focus:outline-none focus-visible:bg-blue-500 mobile-menu"
      >
        Menu
      </button>
      {expanded && (
        <Portal>
          <nav
            className="lg:hidden mobile-popup bg-gray-50"
            id="mobileMenu"
            aria-label="Menu list"
            style={{ top: userInfo.current_admin ? 108 : 50 }}
          >
            {Object.entries(menudefinitions).map(([menu, options]) => (
              <MenuButton
                key={menu}
                name={options.title}
                updateActive={setActive}
                active={activeMenu}
              >
                {showLinksByFeature(options, {
                  Ads: cksnProgrammaticAdsEnabled,
                  "Paid Recommendations": canViewPaidRecommendations
                }).map(submenu => (
                  <Submenu
                    key={submenu.label}
                    label={submenu.label}
                    icon={submenu.icon}
                    color={submenu.iconColor}
                    href={submenu.href}
                    onClick={submenu.onClick}
                    section={menu}
                  />
                ))}
              </MenuButton>
            ))}
            <MenuButton
              name="Account"
              updateActive={setActive}
              active={activeMenu}
            >
              {Object.entries(
                settings(userInfo.current_admin, settingOptions)
              ).map(([menu, options]) => (
                <Submenu key={menu} {...options} section="account" />
              ))}
            </MenuButton>
            <MobileCtaNav userInfo={userInfo} />
          </nav>
        </Portal>
      )}
    </div>
  )
}
